import { useState } from 'react'
import { NavLink, useLocation, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  Breadcrumbs,
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  Container,
  Link,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography
} from '@mui/material'
import {
  Bolt,
  Handyman,
  Check,
  ElectricMeter,
  Hvac,
  Power,
  Thermostat,
  WaterDrop
} from '@mui/icons-material'
import PageHeader from '../components/PageHeader'
import { AssetType } from '../enums/AssetType'
import { usePolicy } from '../contexts/PolicyContext'
import AuroraIcon from '../components/AuroraIcon'

type InstallOption =
  | 'device'
  | 'heater'
  | 'generator'
  | 'ruuvitag_manual'
  | 'aurora'
  | 'spine_multi_measurement'
  | 'installation_kit'
  | 'axioma_w1'

export default function InstallPage() {
  /**
   * The translate function.
   */
  const { t } = useTranslation('installation')

  /**
   * The policy state.
   */
  const { canInstallHeater, canInstallGenerator, isSuperadmin } = usePolicy()

  /**
   * The navigate function.
   */
  const navigate = useNavigate()

  /**
   * The selected option.
   */
  const [selected, setSelected] = useState<InstallOption>()

  /**
   * The query string.
   */
  const { search } = useLocation()

  /**
   * Check if the option is selected.
   */
  function isSelected(option: InstallOption) {
    return selected === option
  }

  /**
   * Handle the "next" action.
   */
  function handleNext() {
    if (selected === 'device') {
      navigate(`/install/device${search}`)
    } else if (selected === 'heater') {
      navigate(`/install/heater${search}`)
    } else if (selected === 'generator') {
      navigate(`/install/generator${search}`)
    } else if (selected === 'ruuvitag_manual') {
      navigate(`/install/ruuvi-tag${search}`)
    } else if (selected === 'aurora') {
      navigate(`/install/aurora`)
    } else if (selected === 'spine_multi_measurement') {
      navigate(`/install/spine-multi-measurement${search}`)
    } else if (selected === 'installation_kit') {
      navigate(`/install/installation-kit${search}`)
    } else if (selected === 'axioma_w1') {
      navigate(`/install/axioma/w1${search}`)
    }
  }

  /**
   * Handle the "cancel" action.
   */
  function handleCancel() {
    navigate('/')
  }

  return (
    <Container maxWidth={false}>
      <PageHeader
        title={t('installAsset')}
        breadcrumbs={
          <Breadcrumbs>
            <Link component={NavLink} to="/">
              {t('frontpage')}
            </Link>
            <Typography>{t('installation')}</Typography>
          </Breadcrumbs>
        }
      />

      <Container maxWidth="md">
        <Card>
          <CardHeader title={t('selectInstallableAsset')} />
          <CardContent sx={{ pt: 0 }}>
            <List>
              <ListItemButton
                selected={isSelected(AssetType.DEVICE)}
                color="success"
                onClick={() => setSelected('device')}
              >
                <ListItemIcon sx={{ minWidth: 42 }}>
                  {isSelected(AssetType.DEVICE) ? <Check color="success" /> : <Bolt />}
                </ListItemIcon>
                <ListItemText
                  primary={t('installation:spineDevice')}
                  secondary={t('installation:deviceTooltip')}
                  primaryTypographyProps={{
                    sx: { order: '0 !important' }
                  }}
                  secondaryTypographyProps={{
                    sx: { order: '1 !important' }
                  }}
                />
              </ListItemButton>

              {isSuperadmin && (
                <ListItemButton
                  selected={isSelected('spine_multi_measurement')}
                  color="success"
                  onClick={() => setSelected('spine_multi_measurement')}
                >
                  <ListItemIcon sx={{ minWidth: 42 }}>
                    {isSelected('spine_multi_measurement') ? (
                      <Check color="success" />
                    ) : (
                      <ElectricMeter />
                    )}
                  </ListItemIcon>
                  <ListItemText
                    primary={t('installation:spineMultiMeasurement')}
                    secondary={t('installation:deviceGroupTooltip')}
                    primaryTypographyProps={{
                      sx: { order: '0 !important' }
                    }}
                    secondaryTypographyProps={{
                      sx: { order: '1 !important' }
                    }}
                  />
                </ListItemButton>
              )}

              {canInstallHeater() && (
                <ListItemButton
                  selected={isSelected('heater')}
                  onClick={() => setSelected('heater')}
                >
                  <ListItemIcon sx={{ minWidth: 42 }}>
                    {isSelected('heater') ? <Check color="success" /> : <Hvac />}
                  </ListItemIcon>
                  <ListItemText
                    primary={t('valve')}
                    secondary={t('heater')}
                    primaryTypographyProps={{
                      sx: { order: '0 !important' }
                    }}
                    secondaryTypographyProps={{
                      sx: { order: '1 !important' }
                    }}
                  />
                </ListItemButton>
              )}

              {canInstallGenerator() && (
                <ListItemButton
                  selected={isSelected('generator')}
                  color="primary"
                  onClick={() => setSelected('generator')}
                >
                  <ListItemIcon sx={{ minWidth: 42 }}>
                    {isSelected('generator') ? <Check color="success" /> : <Power />}
                  </ListItemIcon>
                  <ListItemText
                    primary={t('spineLoad')}
                    secondary={t('spineLoadTooltip')}
                    primaryTypographyProps={{
                      sx: { order: '0 !important' }
                    }}
                    secondaryTypographyProps={{
                      sx: { order: '1 !important' }
                    }}
                  />
                </ListItemButton>
              )}

              <ListItemButton
                selected={isSelected('ruuvitag_manual')}
                color="primary"
                onClick={() => setSelected('ruuvitag_manual')}
              >
                <ListItemIcon sx={{ minWidth: 42 }}>
                  {isSelected('ruuvitag_manual') ? <Check color="success" /> : <Thermostat />}
                </ListItemIcon>
                <ListItemText
                  primary={t('ruuviTag')}
                  secondary={t('ruuviTagCurrentInstallationPath')}
                  primaryTypographyProps={{
                    sx: { order: '0 !important' }
                  }}
                  secondaryTypographyProps={{
                    sx: { order: '1 !important' }
                  }}
                />
              </ListItemButton>

              <ListItemButton
                selected={isSelected('aurora')}
                color="primary"
                onClick={() => setSelected('aurora')}
              >
                <ListItemIcon sx={{ minWidth: 42 }}>
                  {isSelected('aurora') ? (
                    <Check color="success" />
                  ) : (
                    <AuroraIcon width={24} height={24} />
                  )}
                </ListItemIcon>
                <ListItemText
                  primary={t('aurora')}
                  secondary={t('auroraTooltip')}
                  primaryTypographyProps={{
                    sx: { order: '0 !important' }
                  }}
                  secondaryTypographyProps={{
                    sx: { order: '1 !important' }
                  }}
                />
              </ListItemButton>

              <ListItemButton
                key="axioma_w1"
                selected={isSelected('axioma_w1')}
                color="primary"
                onClick={() => setSelected('axioma_w1')}
              >
                <ListItemIcon sx={{ minWidth: 42 }}>
                  {isSelected('axioma_w1') ? (
                    <Check color="success" />
                  ) : (
                    <WaterDrop width={24} height={24} />
                  )}
                </ListItemIcon>
                <ListItemText
                  primary={t('axiomaW1')}
                  secondary={t('installAxiomaW1Tooltip')}
                  primaryTypographyProps={{
                    sx: { order: '0 !important' }
                  }}
                  secondaryTypographyProps={{
                    sx: { order: '1 !important' }
                  }}
                />
              </ListItemButton>

              <ListItemButton
                selected={isSelected('installation_kit')}
                color="primary"
                onClick={() => setSelected('installation_kit')}
              >
                <ListItemIcon sx={{ minWidth: 42 }}>
                  {isSelected('installation_kit') ? (
                    <Check color="success" />
                  ) : (
                    <Handyman width={24} height={24} />
                  )}
                </ListItemIcon>
                <ListItemText
                  primary={t('installationKit')}
                  secondary={t('installInstallationKitTooltip')}
                  primaryTypographyProps={{
                    sx: { order: '0 !important' }
                  }}
                  secondaryTypographyProps={{
                    sx: { order: '1 !important' }
                  }}
                />
              </ListItemButton>
            </List>
          </CardContent>

          <CardActions sx={{ justifyContent: 'flex-end' }}>
            <Button variant="text" color="inherit" onClick={handleCancel}>
              {t('cancel')}
            </Button>

            <Button
              variant="contained"
              color="primary"
              disabled={selected === undefined}
              onClick={handleNext}
            >
              {t('next')}
            </Button>
          </CardActions>
        </Card>
      </Container>
    </Container>
  )
}
